export const dedupeSourceIds = (sourceIds: readonly string[] = []): string[] => {
  return [...new Set(sourceIds)];
};

export type SourceRunPosition = "none" | "single" | "start" | "middle" | "end";

export const getVisibleSourceIdsForRepeatedRun = ({
  sourceIds,
  previousSourceIds,
}: {
  sourceIds?: readonly string[];
  previousSourceIds?: readonly string[];
}): string[] => {
  const uniqueSourceIds = dedupeSourceIds(sourceIds);

  if (uniqueSourceIds.length === 0) {
    return [];
  }

  return isSourceRunContinuation({ sourceIds: uniqueSourceIds, previousSourceIds })
    ? []
    : uniqueSourceIds;
};

export const getSourceRunPosition = ({
  sourceIds,
  previousSourceIds,
  nextSourceIds,
}: {
  sourceIds?: readonly string[];
  previousSourceIds?: readonly string[];
  nextSourceIds?: readonly string[];
}): SourceRunPosition => {
  const uniqueSourceIds = dedupeSourceIds(sourceIds);

  if (uniqueSourceIds.length === 0) {
    return "none";
  }

  const matchesPrevious = haveSameSourceSet(uniqueSourceIds, previousSourceIds);
  const matchesNext = haveSameSourceSet(uniqueSourceIds, nextSourceIds);

  if (matchesPrevious && matchesNext) {
    return "middle";
  }

  if (matchesPrevious) {
    return "end";
  }

  if (matchesNext) {
    return "start";
  }

  return "single";
};

const isSourceRunContinuation = ({
  sourceIds,
  previousSourceIds,
}: {
  sourceIds?: readonly string[];
  previousSourceIds?: readonly string[];
}): boolean => {
  const uniqueSourceIds = dedupeSourceIds(sourceIds);

  return uniqueSourceIds.length > 0 && haveSameSourceSet(uniqueSourceIds, previousSourceIds);
};

const haveSameSourceSet = (
  sourceIds: readonly string[],
  comparisonSourceIds: readonly string[] = []
): boolean => {
  const uniqueSourceIds = dedupeSourceIds(sourceIds);
  const uniqueComparisonSourceIds = dedupeSourceIds(comparisonSourceIds);

  if (uniqueSourceIds.length !== uniqueComparisonSourceIds.length) {
    return false;
  }

  const comparisonSet = new Set(uniqueComparisonSourceIds);

  return uniqueSourceIds.every(sourceId => comparisonSet.has(sourceId));
};
